import { getActiveStore, setActiveStore as persistStore } from "@/lib/scope";
import WriteButton from "@/components/WriteButton";
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Store as StoreIcon, CheckCircle2 } from "lucide-react";

export default function Stores() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const active = getActiveStore();

  const load = async () => {
    try { setItems((await api.get("/stores")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!name.trim()) return toast.error("Nama toko wajib diisi");
    try {
      if (editing) await api.put(`/stores/${editing.id}`, { name });
      else await api.post("/stores", { name });
      toast.success("Toko disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus toko "${s.name}"?`)) return;
    try { await api.delete(`/stores/${s.id}`); toast.success("Toko dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setActive = (s) => {
    persistStore(s.id);
    toast.success(`Toko aktif: ${s.name}`);
    setTimeout(() => window.location.reload(), 500);
  };

  return (
    <>
      <PageHeader title="Toko / Cabang" subtitle={`${items.length} toko · stok terpisah per toko`}>
        <WriteButton data-testid="add-store-btn" onClick={() => { setEditing(null); setName(""); setOpen(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2">
          <Plus className="h-4 w-4" /> Tambah Toko
        </WriteButton>
      </PageHeader>
      <div className="flex-1 overflow-y-auto p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-list">
          {items.map((s) => (
            <div key={s.id} data-testid={`store-card-${s.id}`} className={`rounded-xl border p-4 ${active === s.id ? "border-emerald-500/60 bg-emerald-500/10" : "border-slate-200 bg-slate-50"}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center"><StoreIcon className="h-5 w-5" /></div>
                  <div>
                    <div className="font-semibold flex items-center gap-2">{s.name} {s.is_default && <span className="text-[10px] bg-slate-200 px-1.5 py-0.5 rounded">default</span>}</div>
                    {active === s.id && <div className="text-[11px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Toko aktif</div>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <WriteButton plain data-testid={`edit-store-${s.id}`} onClick={() => { setEditing(s); setName(s.name); setOpen(true); }} className="h-8 w-8 rounded-lg bg-[#F7F9FB] hover:bg-slate-200 flex items-center justify-center"><Pencil className="h-4 w-4 text-slate-600" /></WriteButton>
                  {!s.is_default && <WriteButton plain data-testid={`delete-store-${s.id}`} onClick={() => remove(s)} className="h-8 w-8 rounded-lg bg-[#F7F9FB] hover:bg-red-500/20 flex items-center justify-center"><Trash2 className="h-4 w-4 text-red-600" /></WriteButton>}
                </div>
              </div>
              {active !== s.id && (
                <Button data-testid={`activate-store-${s.id}`} onClick={() => setActive(s)} variant="outline" className="w-full mt-3 h-9 border-slate-300 bg-transparent hover:bg-[#F7F9FB] text-xs">Jadikan Toko Aktif</Button>
              )}
            </div>
          ))}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="store-form-dialog" className="bg-[#F7F9FB] border-slate-300 text-slate-800 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Toko" : "Tambah Toko"}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Nama Toko</Label>
            <Input data-testid="store-name-input" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#F7F9FB] border-slate-300" placeholder="mis. Cabang Pasar Baru" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB]">Batal</Button>
            <WriteButton data-testid="save-store-btn" onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold">Simpan</WriteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
